import React, { useContext, useMemo, useRef, useCallback } from 'react'
import { Platform, ScrollView, StyleSheet, View } from 'react-native'
import { FlashList } from '@shopify/flash-list'
import { Spinner, Text } from '@ui-kitten/components'
import { useTranslation } from 'react-i18next'
import { useAtomValue } from 'jotai'
import MessageBubble from './MessageBubble'
import ProductDraftCard from './ProductDraftCard'
import WelcomeQuickReplies from './WelcomeQuickReplies'
import { organizationAtom, organizationSubscriptionInfoAtom, userRoleAtom } from '../../../global/AtomsProvider'
import { UserContext } from '../../../global/UserProvider'
import { canCreateProducts } from '../../../global/Support/productDraftPrefill'
import { canUseQuickReply } from '../../../global/Support/quickReplies'

const isWeb = Platform.OS === 'web'

export default function MessageList({ messages, loading, onOpenProductDraft, onQuickReply }) {
  const { t } = useTranslation()
  // ATL-1323: an agent's product proposal is offered only to someone who can create products — the
  // same rule the product screen and the backend apply.
  const userRole = useAtomValue(userRoleAtom)
  const userContext = useContext(UserContext)
  const showDraftCards = canCreateProducts(userRole, userContext?.[0]?.user)
  // ACT-QUICK-REPLY-001: the welcome's buttons, limited to the ones this person can use. Absent
  // `onQuickReply` (no navigator, e.g. the blocked-app screen) shows none.
  const organization = useAtomValue(organizationAtom)
  const subscription = useAtomValue(organizationSubscriptionInfoAtom)
  const access = { userRole, user: userContext?.[0]?.user, organization, subscription }
  const usableQuickReplies = (message) =>
    onQuickReply && message.quickReplies ? message.quickReplies.filter((r) => canUseQuickReply(r, access)) : []
  // Persistent greeting (Fin-style): the conversation's first/oldest message. Synthetic, so it
  // stays in place once the user sends — without it, the first real message floats alone with no
  // context above it. The inverted list renders the array tail at the top, so welcome goes last.
  const welcome = useMemo(
    () => ({
      id: '__welcome__',
      authorType: 'ai_agent',
      authorId: null,
      agentName: t('support.support'),
      body: t('support.welcome'),
      createdAt: '',
      attachments: [],
    }),
    [t],
  )

  // "Reply is coming" hint: shown while the newest message is the customer's (a successfully-sent
  // message awaiting an agent reply). Synthetic and non-persisted — never written to the
  // conversation, so it can't interfere with the backend debounce / webhook payload. The inverted
  // list renders index 0 at the bottom, so this sits just under the customer's latest message.
  const last = messages[messages.length - 1]
  const awaiting = !!last && last.authorType === 'customer'

  // native: inverted order ([waiting, ...reversed messages, welcome]); web: chronological ([welcome, ...messages, waiting])
  const data = useMemo(() => {
    const reversed = [...messages].reverse()
    if (isWeb) {
      const base = [welcome, ...messages]
      return awaiting ? [...base, { id: '__waiting__' }] : base
    }
    return awaiting ? [{ id: '__waiting__' }, ...reversed, welcome] : [...reversed, welcome]
  }, [messages, welcome, awaiting])

  const listRef = useRef(null)
  const scrollEnd = useCallback(() => {
    if (isWeb && listRef.current) listRef.current.scrollToEnd?.({ animated: false })
  }, [])

  const renderItem = ({ item }) => {
    const quickReplies = usableQuickReplies(item)
    return item.id === '__waiting__' ? (
      <View style={styles.waiting}>
        <Text appearance="hint" category="c1" style={styles.waitingText}>
          {t('support.awaitingReply')}
        </Text>
      </View>
    ) : item.actionId && showDraftCards ? (
      <View>
        <MessageBubble message={item} />
        <ProductDraftCard key={item.actionId} actionId={item.actionId} onOpenDraft={onOpenProductDraft} />
      </View>
    ) : quickReplies.length > 0 ? (
      <View>
        <MessageBubble message={item} />
        <WelcomeQuickReplies messageId={item.id} replies={quickReplies} onQuickReply={onQuickReply} />
      </View>
    ) : (
      <MessageBubble message={item} />
    )
  }

  if (loading && messages.length === 0) {
    return (
      <View style={styles.centered}>
        <Spinner />
      </View>
    )
  }

  // Empty chat: top-align the lone greeting (a one-item inverted list would pin it to the bottom).
  if (!loading && messages.length === 0) {
    return (
      <ScrollView style={styles.flex} contentContainerStyle={styles.welcomeContent} showsVerticalScrollIndicator={false}>
        <MessageBubble message={welcome} />
      </ScrollView>
    )
  }

  return (
    <FlashList
      ref={listRef}
      data={data}
      inverted={!isWeb}
      renderItem={renderItem}
      keyExtractor={(item) => item.id}
      estimatedItemSize={64}
      contentContainerStyle={styles.content}
      showsVerticalScrollIndicator={false}
      onContentSizeChange={scrollEnd}
    />
  )
}

const styles = StyleSheet.create({
  flex: { flex: 1 },
  centered: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 28 },
  welcomeContent: { paddingTop: 16, paddingBottom: 8 },
  content: { paddingVertical: 14 },
  waiting: { alignItems: 'center', paddingVertical: 6, paddingHorizontal: 16 },
  waitingText: { textAlign: 'center' },
})
